import type { JobRun } from "../types/job";

/** Status em que a execução ainda não terminou — a lista se atualiza sozinha enquanto houver um. */
export const LIVE_RUN_STATUSES = new Set(["queued", "pending", "running", "waiting", "resuming"]);

export type RunOutcome = "live" | "completed" | "failed" | "cancelled";

/** Como a linha do histórico mostra o status: o da execução, agrupado como o filtro. */
export function runOutcome(status: string): RunOutcome {
  if (LIVE_RUN_STATUSES.has(status)) return "live";
  if (status === "completed") return "completed";
  if (status === "cancelled") return "cancelled";
  return "failed";
}

/** Espera antes de reconectar o SSE das execuções: dobra a cada falha, até 30 s. */
export function reconnectDelay(attempt: number): number {
  return Math.min(1_000 * 2 ** attempt, 30_000);
}

/** "8 s", "3 min 5 s", "1 h 2 min" — curto, para caber na coluna. */
export function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return "—";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const rest = seconds % 60;
    return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export interface AgentReport {
  summary?: string;
  pending?: unknown;
  actions?: Array<{ tool?: string; write?: boolean; is_error?: boolean }>;
  writes?: number;
  usage?: { modelCalls?: number; inputTokens?: number; outputTokens?: number };
}

/** O relatório do passo do agente de uma execução, ou vazio quando ainda não há. */
export function agentReportOf(run: JobRun | undefined): { report: AgentReport; pending: string[]; error: string | null } {
  const step = run?.steps.find((s) => s.tool === "agentic");
  const report = (step?.output ?? {}) as AgentReport;
  const pending = Array.isArray(report.pending)
    ? report.pending.map(String)
    : typeof report.pending === "string" && report.pending
      ? [report.pending]
      : [];
  return { report, pending, error: run?.error || step?.error || null };
}
